import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
    isMobileDevice, 
    isIOSDevice, 
    isAndroidDevice, 
    getDeviceType, 
    supportsFileSystemAccess 
} from '../utils/deviceUtils';

export interface DeviceInfo {
    isMobile: boolean;
    isIOS: boolean;
    isAndroid: boolean;
    deviceType: 'ios' | 'android' | 'mobile' | 'desktop';
    supportsFileSystemAccess: boolean;
    userAgent: string;
}

interface DeviceContextType {
    deviceInfo: DeviceInfo;
    refreshDeviceInfo: () => void;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export const DeviceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => ({
        isMobile: isMobileDevice(),
        isIOS: isIOSDevice(),
        isAndroid: isAndroidDevice(),
        deviceType: getDeviceType(),
        supportsFileSystemAccess: supportsFileSystemAccess(),
        userAgent: navigator.userAgent,
    }));

    const refreshDeviceInfo = () => {
        setDeviceInfo({
            isMobile: isMobileDevice(),
            isIOS: isIOSDevice(),
            isAndroid: isAndroidDevice(),
            deviceType: getDeviceType(),
            supportsFileSystemAccess: supportsFileSystemAccess(),
            userAgent: navigator.userAgent,
        });
    };

    useEffect(() => {
        // Refresh device info on mount and when window resizes
        const handleResize = () => {
            refreshDeviceInfo();
        };

        window.addEventListener('resize', handleResize);
        
        // Also refresh on orientation change for mobile devices
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    return (
        <DeviceContext.Provider value={{ deviceInfo, refreshDeviceInfo }}>
            {children}
        </DeviceContext.Provider>
    );
};

export const useDevice = () => {
    const context = useContext(DeviceContext);
    if (!context) {
        throw new Error('useDevice must be used within a DeviceProvider');
    }
    return context;
}; 